import { useState, useEffect, MouseEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { DropDownInfo } from "../../services/interface/IHome";
// import WDAdvantage from "../../assets/wdadvantage.svg";
import WDAdvantage from "../../assets/demandbrain.png";
import HomeLogo from "../../assets/Home.svg";
import UpArrow from "../../assets/uparrow.svg";
import Down from "../../assets/downarrow.svg";
import DropDown from "../DropDown";
import { useMsal } from "@azure/msal-react";
import {
  AccountInfo,
  InteractionRequiredAuthError,
  InteractionStatus,
} from "@azure/msal-browser";
import { callMsGraph } from "../../services/msalAuth/MSALGraph";
import { loginRequest } from "../../services/msalAuth/authConfig";
import companylogo from "../../assets/companylogo.svg";
import convergeConsumerLogo from "../../assets/converge-consumer.svg";
import inventoryManagementLogo from "../../assets/inventory-management.svg";
import { Divider } from "rsuite";
import useWdEdgeConsolidatedSource from "../../hooks/useWdEdgeConsolidatedSource";
import ConsolidatedHeader from "../ConsolidatedHeader";

const getDropdownInfo = (healthcareValue: number): DropDownInfo[] => [
  {
    isOpen: false,
    isHighLighted: false,
    alertKey: "/KPI",
    options: ["KPI"],
  },
  {
    isOpen: false,
    isHighLighted: false,
    alertKey: "/forecast/forecast-alerts",
    options: [
      "High Over-Forecasting",
      "High Under-Forecasting",
      "Low Forecast Accuracy",
      "High Outlier Correction",
      "Volatile Forecast",
    ],
  },
  {
    isOpen: false,
    isHighLighted: false,
    alertKey: "demand-planning",
    options: [
      "Forecast Overview",
      "Forecast Deep Dive",
      "Forecast Segmentation",
      "Feature Importance",
      "Accuracy Deep Dive",
      "Category Level Seasonal Trend",
    ],
  },
  {
    isOpen: false,
    isHighLighted: false,
    alertKey: "/inventory/inventory-alerts",
    options: [
      "Potential Overstock",
      "Potential Stockout",
      "Ageing Inventory",
      "Devalued Inventory",
      "High Stockout",
    ],
  },
  {
    isOpen: false,
    isHighLighted: false,
    alertKey: "Inventory & Replenishment",
    options: ["On Hand vs Target", "Inventory and SOQ", "Inventory Overview"],
  },
  {
    isHighLighted: false,
    isOpen: false,
    alertKey: "collaborative-planning",
    options: ["POS vs Shipped", "Consensus Planning"],
  },
  // Commented out Promotions Assessment
  {
    isOpen: false,
    isHighLighted: false,
    alertKey: "promotions-assessment",
    options:
      healthcareValue === 1
        ? ["Cannibalization", "Ad Effectiveness"]
        : ["Ad Effectiveness"],
  },
];

const Navbar = () => {
  const { isSourceWdEdgeConsolidated } = useWdEdgeConsolidatedSource();
  const { instance, inProgress } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const isDemandBrainQueryTrue =
    new URLSearchParams(location.search).get("demandbrain")?.toLowerCase() ===
    "true";
  const [userEmail, setUserEmail] = useState<string>("");
  const [graphData, setGraphData] = useState<any>(null);
  const [healthcareValue, setHealthcareValue] = useState<number>(
    Number(localStorage.getItem("healthcareValue")) || 0,
  );
  const [isOpenDropdown, setIsOpenDropdown] = useState<DropDownInfo[]>(
    getDropdownInfo(healthcareValue),
  );
  const [route, setRoute] = useState<string>(
    location?.pathname?.split("/")[location?.pathname?.split("/").length - 1],
  );

  const navigateToHandler = (value: string | null) => {
    if (value) {
      navigate(value);
    }
  };

  const dropDownHandler = (e: MouseEvent, key: number) => {
    const localState = structuredClone(isOpenDropdown);
    localState.forEach((dropdown: DropDownInfo, index: number) => {
      dropdown.isOpen = index === key ? !dropdown.isOpen : false;
    });
    setIsOpenDropdown(localState);
  };

  useEffect(() => {
    const handleStorageChange = () => {
      const newHealthcareValue =
        Number(localStorage.getItem("healthcareValue")) || 0;
      setHealthcareValue(newHealthcareValue);
      setIsOpenDropdown(getDropdownInfo(newHealthcareValue));
    };

    // Listen for storage events
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (!graphData && inProgress === InteractionStatus.None) {
      callMsGraph()
        .then((response) => {
          window.localStorage.setItem("userEmail", response.mail);
          setUserEmail(response.mail);
          setGraphData(response);
          let authHandler = localStorage.getItem("authHandler");
          if (authHandler && authHandler === "1") {
            localStorage.setItem("authHandler", "0");
          }
          localStorage.setItem("name", response?.displayName);
        })
        .catch((e) => {
          if (e instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect({
              ...loginRequest,
              account: instance.getActiveAccount() as AccountInfo,
            });
          }
        });
    }
  }, [inProgress, graphData, instance]);

  useEffect(() => {
    const pathSegments = location?.pathname?.split("/");
    const lastSegment = pathSegments[pathSegments.length - 1];
    setRoute(lastSegment);

    const localState = structuredClone(isOpenDropdown);
    const healthcareValue =
      Number(localStorage.getItem("healthcareValue")) || 0;

    localState.forEach((dropdown: DropDownInfo, index: number) => {
      dropdown.isOpen = false;
      dropdown.isHighLighted = false;
    });

    // Check for KPI first (Index 0)
    if (pathSegments.includes("PerformanceMetrics")) {
      localState[0].isHighLighted = true; // KPI is the first dropdown item (index 0)
    }
    // Forecast Alerts (Index 1)
    else if (pathSegments.includes("forecast")) {
      localState[1].isHighLighted = true; // Forecast Alerts is index 1
    }
    // Demand Planning - formerly Demand Forecasting (Index 2)
    else if (
      [
        "ForecastOverview",
        "ForecastDeepDive",
        "ForecastSegmentation",
        "FeatureImportance",
        "AccuracyDeepDive",
      ].includes(pathSegments[1])
    ) {
      localState[2].isHighLighted = true; // Demand Planning is index 2
    }
    // Inventory Alerts (Index 3)
    else if (pathSegments.includes("inventory")) {
      localState[3].isHighLighted = true; // Inventory Alerts is index 3
    }
    // Inventory & Replenishment (Index 4)
    else if (
      ["OnHandVsTarget", "InventoryAndSoq", "InventoryOverview"].includes(
        pathSegments[1],
      )
    ) {
      localState[4].isHighLighted = true; // Inventory & Replenishment is index 4
    }
    // Collaborative Planning (Index 5)
    else if (["ConsensusPlanning", "PosVsShipped"].includes(pathSegments[1])) {
      localState[5].isHighLighted = true; // Collaborative Planning is index 5
    }

    // Commented out Promotions Assessment highlighting logic
    // else if (healthcareValue === 1) {
    //     if (["AdEffectiveness"].includes(pathSegments[1])) {
    //         localState[6].isHighLighted = true; // Promotions Assessment is index 6
    //     }
    // } else {
    //     if (["Cannibalization", "AdEffectiveness"].includes(pathSegments[1])) {
    //         localState[6].isHighLighted = true; // Promotions Assessment is index 6
    //     }
    // }

    setIsOpenDropdown(localState);
  }, [location]);

  return (
    <div className="flex flex-col justify-between shadow-normal border-b border-[#BEBEBE] p-1">
      <div className="flex w-full justify-between px-[2%] py-[0.2vw] h-full">
        <div className="logoContainer">
          {!isDemandBrainQueryTrue &&
            (isSourceWdEdgeConsolidated ? (
              <span className="font-[700] text-[#03234d]">
                Margin and Service Improvement Control Tower
              </span>
            ) : (
              <img
                className="converge-consumer-logo h-[2.5vh] w-full" //
                src={convergeConsumerLogo}
                alt="Converge Consumer Logo"
              />
            ))}
          {!isDemandBrainQueryTrue && (
            <span className="marginBrain">
              <Divider className="line" vertical />
            </span>
          )}
          <div className="sublogoContainer">
            <img
              className="inventoryManagementLogo w-full" //
              src={inventoryManagementLogo}
              alt="Inventory Management Logo"
            />
          </div>
        </div>
      </div>

      {!isDemandBrainQueryTrue && (
        <div className="flex w-full px-[2%] gap-1 mt-1 bg-[#F1F1F1]">
          {isSourceWdEdgeConsolidated ? (
            <ConsolidatedHeader />
          ) : (
            <>
              <div
                className={`flex justify-start items-center cursor-pointer px-[1vw] py-[1.6vh] gap-2 
                    ${route === "" ? "border-b-[0.2vw] border-[#86BC25]" : ""}`}
                onClick={() => {
                  navigateToHandler("/");
                }}
              >
                <img className="w-[0.6vw]" src={HomeLogo} alt="Home" />
                <p
                  className={`text-fs16 font-normal navbar-menu ${route === "" ? "text-deloitteGreen font-bold" : ""}`}
                >
                  Home
                </p>
              </div>

              {isOpenDropdown.map((dropdown, index) => (
                <div
                  key={index}
                  className={`flex justify-start items-center gap-1 px-[1vw] py-[1.6vh] relative 
                        ${dropdown.isOpen || dropdown.isHighLighted ? "border-b-[0.2vw] border-[#86BC25]" : ""}  ${dropdown.disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                  onClick={(e) => {
                    if (!dropdown.disabled) dropDownHandler(e, index);
                  }}
                >
                  <p
                    className={`text-fs16 font-normal navbar-menu mx-[0.7vw]
                        ${dropdown.isOpen === true ? "text-deloitteGreen font-bold" : ""}`}
                  >
                    {dropdown.alertKey
                      .split("/")
                      .pop()
                      ?.split("-")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1),
                      )
                      .join(" ")}
                  </p>
                  <img
                    className="w-[0.7vw]"
                    src={dropdown.isOpen ? UpArrow : Down}
                    alt="Dropdown"
                  />
                  {dropdown.isOpen && <DropDown dropdown={dropdown} />}
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Navbar;
